import { Component, input, OnInit, OnChanges, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Project } from 'src/app/entities/project/project';
import { NgxEchartsDirective } from 'ngx-echarts';
import { MatTableModule } from '@angular/material/table';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { EChartsCoreOption } from 'echarts/core';
import { TooltipFormatterParams } from 'src/app/models/echarts/tooltip.model';
import { TimeTrack } from 'src/app/entities/time-track/time-track';
import { User } from 'src/app/entities/user/user';

/**
 * Enum to define the available view types for the summary display
 */
enum ViewType {
  Projects = 'projects',
  Clients = 'clients',
  Users = 'users'
}

/**
 * Interface that defines the structure of a summary item
 * Used for both project and client summaries
 */
interface Summary {
  /** Name of the project or client */
  name: string;
  /** Color used for visual representation */
  color: string;
  /** Total hours spent */
  hours: number;
  /** Percentage of total time */
  percentage: number;
}

interface DetailedSummary {
  /** Parent entity name (project/client/user) */
  parentName: string;
  /** Time track title */
  trackTitle: string;
  /** Color used for visual representation */
  color: string;
  /** Hours spent on this track */
  hours: number;
  /** Percentage of total time */
  percentage: number;
}

@Component({
  selector: 'app-summary-resume',
  imports: [
    CommonModule,
    NgxEchartsDirective,
    ReactiveFormsModule,
    MatTableModule,
    MatFormFieldModule,
    MatSelectModule
  ],
  templateUrl: './summary-resume.component.html',
  styleUrl: './summary-resume.component.scss'
})
/**
 * Component that displays a summary of time tracking data
 * Shows both a table and a donut chart representation
 */
export class SummaryResumeComponent implements OnInit, OnChanges {
  /** Array of projects with their time tracking data */
  projects = input<Project[]>([]);

  /** Collection of all time tracking entries */
  timeTracks = input<TimeTrack[]>([]);

  /** List of all users in the system */
  users = input<User[]>([]);

  periodStart = input.required<Date>();

  periodEnd = input.required<Date>();

  /** Map to store consistent colors for users across renders */
  private userColors = new Map<string, string>();

  /** Form group for the view type selector */
  protected viewForm = new FormGroup({
    viewType: new FormControl(ViewType.Projects, Validators.required)
  });

  /** Expose enum to template */
  readonly viewTypes = ViewType;

  /** Chart configuration options */
  chartOptions: EChartsCoreOption = {};

  /** Detailed chart configuration options */
  detailedChartOptions: EChartsCoreOption = {};

  periodEffect = effect(() => {
    if (this.periodEnd() && this.periodStart()) {
      this.updateChartOptions();
    }
  })

  ngOnInit(): void {
    this.viewForm.get('viewType')?.valueChanges.subscribe(() => {
      this.updateChartOptions();
    });
  }

  ngOnChanges(): void {
    // Only update charts if we have data
    if (this.timeTracks().length > 0) {
      this.updateChartOptions();
      this.updateDetailedChartOptions();
    }
  }

  /**
   * Filters time tracks based on the current date range
   * @returns Filtered time tracks array
   */
  private getFilteredTimeTracks(): TimeTrack[] {
    // Get all time tracks passed to the component
    const tracks = this.timeTracks();

    // If there are no tracks, return empty array
    if (!tracks || tracks.length === 0) {
      return [];
    }

    // Find the earliest and latest dates in the tracks
    // This is a fallback if we can't determine the date range from other sources
    let earliestDate = new Date(tracks[0].from);
    let latestDate = new Date(tracks[0].to || new Date());

    tracks.forEach(track => {
      if (track.from < earliestDate) earliestDate = new Date(track.from);
      if (track.to && track.to > latestDate) latestDate = new Date(track.to || new Date());
    });

    // Try to get date range from URL parameters or other sources
    // This assumes the tracks are already filtered by the parent component

    return tracks;
  }

  /**
   * Converts seconds to hours with one decimal precision
   * @param seconds - Number of seconds to convert
   * @returns Hours with one decimal place precision
   */
  /**
   * Converts seconds to hours with appropriate precision
   * For very small values (less than 0.1 hours), returns the actual value without rounding
   * @param seconds - Number of seconds to convert
   * @returns Hours with appropriate precision
   */
  private secondsToHours(seconds: number): number {
    const hours = seconds / 3600;
    // If less than 6 minutes (0.1 hours), use higher precision
    return hours < 0.1 ? hours : Math.round(hours * 10) / 10;
  }

  /**
   * Computes the summaries based on the current view type
   * @returns Array of summaries with hours and percentages
   */
  /**
   * Computes and returns summaries based on the current view type (Projects/Clients/Users)
   * - For Projects: Shows time spent per project
   * - For Clients: Aggregates project times by client
   * - For Users: Shows time spent per user based on their time tracks
   * 
   * @returns Array of Summary objects sorted by hours in descending order
   */
  //private getProjectsWithTracks(): Project[] {
  //  const filteredTracks = this.getFilteredTimeTracks();
  //  return this.projects().filter(project =>
  //    filteredTracks.some(track => track.project?.id === project.id)
  //  );
  //}

  private getProjectSummaries(): Summary[] {
    // Get projects that have time tracks in the filtered set
    const filteredTracks = this.getFilteredTimeTracks();

    // Create a map to store total time spent per project
    const projectTimeMap = new Map<string, number>();

    // Calculate time spent for each project based on filtered tracks
    filteredTracks.forEach(track => {
      if (track.project && track.time_spent) {
        const projectId = track.project.id;
        const currentTime = projectTimeMap.get(projectId) || 0;
        projectTimeMap.set(projectId, currentTime + track.time_spent);
      }
    });

    // Get all projects that have time in the filtered tracks
    const projectsWithTracks = this.projects().filter(project =>
      projectTimeMap.has(project.id)
    );

    // Calculate total seconds across all projects
    const totalSeconds = Array.from(projectTimeMap.values()).reduce((acc, time) => acc + time, 0);

    // Create summary objects for each project
    return projectsWithTracks.map(project => ({
      name: project.name,
      color: project.color,
      hours: this.secondsToHours(projectTimeMap.get(project.id) || 0),
      percentage: this.calculatePercentage(projectTimeMap.get(project.id) || 0, totalSeconds)
    }));
  }

  public getDetailedProjectSummaries(): DetailedSummary[] {
    let colorIndex = 0;
    const summaries: DetailedSummary[] = [];
    const filteredTracks = this.getFilteredTimeTracks();

    // Get all projects that have tracks in the filtered set
    const projectIds = new Set<string>();
    filteredTracks.forEach(track => {
      if (track.project) {
        projectIds.add(track.project.id);
      }
    });

    const projectsWithTracks = this.projects().filter(project =>
      projectIds.has(project.id)
    );

    for (const project of projectsWithTracks) {
      const projectTracks = filteredTracks.filter(track => track.project?.id === project.id);

      for (const track of projectTracks) {
        if (track.time_spent) {
          const color = this.getTrackColor(colorIndex++);
          summaries.push({
            parentName: project.name,
            trackTitle: track.title,
            color: color,
            hours: this.secondsToHours(track.time_spent),
            percentage: 0
          });
        }
      }
    }

    const totalSeconds = summaries.reduce((acc, summary) => acc + (summary.hours * 3600), 0);
    summaries.forEach(summary => {
      summary.percentage = this.calculatePercentage(summary.hours * 3600, totalSeconds);
    });

    return summaries;
  }

  /**
   * Generates a unique color based on the index.
   * This uses a simple hue rotation to create distinct colors.
   *
   * @param index The index of the color to generate.
   * @returns A hexadecimal color string.
   */
  private getTrackColor(index: number): string {
    const hue = (index * 137.508) % 360; // use golden angle approximation
    return `hsl(${hue}, 70%, 60%)`;
  }

  private getClientSummaries(): Summary[] {
    const clientMap = new Map<string, Summary>();
    const filteredTracks = this.getFilteredTimeTracks();

    // Calculate time spent per client based on filtered tracks
    filteredTracks.forEach(track => {
      if (track.project?.client && track.time_spent) {
        const clientId = track.project.client.id;

        if (!clientMap.has(clientId)) {
          clientMap.set(clientId, {
            name: track.project.client.name,
            color: track.project.color,
            hours: 0,
            percentage: 0
          });
        }

        const summary = clientMap.get(clientId)!;
        summary.hours += this.secondsToHours(track.time_spent);
      }
    });

    const totalSeconds = Array.from(clientMap.values())
      .reduce((acc, summary) => acc + (summary.hours * 3600), 0);

    for (const summary of clientMap.values()) {
      summary.percentage = this.calculatePercentage(summary.hours * 3600, totalSeconds);
    }

    return Array.from(clientMap.values());
  }

  private getDetailedClientSummaries(): DetailedSummary[] {
    const summaries: DetailedSummary[] = [];
    const filteredTracks = this.getFilteredTimeTracks();
    let colorIndex = 0;

    for (const track of filteredTracks) {
      if (track.project?.client && track.time_spent) {
        const color = this.getTrackColor(colorIndex++);
        summaries.push({
          parentName: track.project.client.name,
          trackTitle: track.title,
          color: color,
          hours: this.secondsToHours(track.time_spent),
          percentage: 0
        });
      }
    }

    const totalSeconds = summaries.reduce((acc, summary) => acc + (summary.hours * 3600), 0);
    summaries.forEach(summary => {
      summary.percentage = this.calculatePercentage(summary.hours * 3600, totalSeconds);
    });

    return summaries;
  }

  private getUserSummaries(): Summary[] {
    const userSummaries = new Map<string, Summary>();
    const filteredTracks = this.getFilteredTimeTracks();

    for (const user of this.users()) {
      if (!this.userColors.has(user.id)) {
        this.userColors.set(user.id, '#' + Math.floor(Math.random() * 16777215).toString(16));
      }

      const userTracks = filteredTracks.filter(track => track.author?.id === user.id);
      const totalUserSeconds = userTracks.reduce((acc, track) => acc + (track.time_spent || 0), 0);

      // Only include users who have time tracks in the filtered period
      if (totalUserSeconds > 0) {
        userSummaries.set(user.id, {
          name: user.name,
          color: this.userColors.get(user.id)!,
          hours: this.secondsToHours(totalUserSeconds),
          percentage: 0
        });
      }
    }

    const totalSeconds = Array.from(userSummaries.values())
      .reduce((acc, summary) => acc + (summary.hours * 3600), 0);

    userSummaries.forEach(summary => {
      summary.percentage = this.calculatePercentage(summary.hours * 3600, totalSeconds);
    });

    return Array.from(userSummaries.values());
  }

  private getDetailedUserSummaries(): DetailedSummary[] {
    const summaries: DetailedSummary[] = [];
    const filteredTracks = this.getFilteredTimeTracks();
    let colorIndex = 0;

    for (const track of filteredTracks) {
      if (track.author && track.time_spent) {
        if (!this.userColors.has(track.author.id)) {
          this.userColors.set(track.author.id, '#' + Math.floor(Math.random() * 16777215).toString(16));
        }

        const color = this.getTrackColor(colorIndex++);
        summaries.push({
          parentName: track.author.name,
          trackTitle: track.title,
          color: color,
          hours: this.secondsToHours(track.time_spent),
          percentage: 0
        });
      }
    }

    const totalSeconds = summaries.reduce((acc, summary) => acc + (summary.hours * 3600), 0);
    summaries.forEach(summary => {
      summary.percentage = this.calculatePercentage(summary.hours * 3600, totalSeconds);
    });

    return summaries;
  }

  get summaries(): Summary[] {
    let summaries: Summary[];

    switch (this.viewForm.get('viewType')?.value) {
      case ViewType.Projects:
        summaries = this.getProjectSummaries();
        break;
      case ViewType.Clients:
        summaries = this.getClientSummaries();
        break;
      default:
        summaries = this.getUserSummaries();
    }

    return summaries.sort((a, b) => b.hours - a.hours);
  }

  /**
   * Calculates the total hours across all summaries
   * Used for both displaying the total and calculating percentages
   * 
   * @returns Total hours as a number with one decimal precision
   */
  get totalHours(): number {
    return this.summaries.reduce((acc, summary) => acc + summary.hours, 0);
  }

  /**
   * Updates the chart configuration when data or view type changes
   * Triggers a re-render of the donut chart
   */
  private updateChartOptions(): void {
    this.chartOptions = this.getChartOptions();
    this.updateDetailedChartOptions();
  }

  private updateDetailedChartOptions(): void {
    let detailedSummaries: DetailedSummary[];

    switch (this.viewForm.get('viewType')?.value) {
      case ViewType.Projects:
        detailedSummaries = this.getDetailedProjectSummaries();
        break;
      case ViewType.Clients:
        detailedSummaries = this.getDetailedClientSummaries();
        break;
      default:
        detailedSummaries = this.getDetailedUserSummaries();
    }

    this.detailedChartOptions = {
      tooltip: {
        trigger: 'item',
        formatter: (params: TooltipFormatterParams<DetailedSummary>) => {
          const formatTimeValue = (hours: number): string => {
            if (hours < 0.01) { // Less than 36 seconds
              const seconds = Math.round(hours * 3600);
              return `${seconds}s`;
            } else if (hours < 0.1) { // Less than 6 minutes
              const minutes = Math.round(hours * 60);
              return `${minutes}m`;
            } else {
              return `${hours.toFixed(1)}h`;
            }
          };

          const formatPercentage = (num: number): string =>
            num % 1 > 0 ? num.toFixed(1) : Math.floor(num).toString();

          const timeDisplay = formatTimeValue(params.data.hours);
          const percentage = formatPercentage(params.data.percentage);
          return `${params.data.parentName} - ${params.data.trackTitle}: ${timeDisplay} (${percentage}%)`;
        }
      },
      series: [
        {
          type: 'pie',
          radius: ['40%', '70%'],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 10,
            borderColor: '#fff',
            borderWidth: 2
          },
          label: {
            show: false,
          },
          emphasis: {
            label: {
              show: true,
              fontSize: 16,
              fontWeight: 'bold'
            }
          },
          labelLine: {
            show: false
          },
          data: detailedSummaries.map(({ parentName, trackTitle, hours, color, percentage }) => ({
            value: hours,
            name: `${parentName} - ${trackTitle}`,
            itemStyle: {
              color
            },
            parentName,
            trackTitle,
            hours,
            percentage
          }))
        }
      ]
    };
  }


  /**
   * Calculates percentage with a safety check for zero division
   * @param value - The value to calculate percentage for
   * @param total - The total value representing 100%
   * @returns Percentage as a number between 0 and 100
   */
  private calculatePercentage(value: number, total: number): number {
    return total > 0 ? (value / total) * 100 : 0;
  }

  /**
   * Creates the configuration object for the ECharts donut chart
   * Configures:
   * - Tooltip formatting for hover information
   * - Chart layout and styling
   * - Data series with colors and labels
   * 
   * @returns Complete ECharts configuration object
   */
  private getChartOptions(): EChartsCoreOption {
    return {
      tooltip: {
        trigger: 'item',
        formatter: (params: TooltipFormatterParams<Summary>) => {
          const formatTimeValue = (hours: number): string => {
            if (hours < 0.01) { // Less than 36 seconds
              const seconds = Math.round(hours * 3600);
              return `${seconds}s`;
            } else if (hours < 0.1) { // Less than 6 minutes
              const minutes = Math.round(hours * 60);
              return `${minutes}m`;
            } else {
              return `${hours.toFixed(1)}h`;
            }
          };

          const formatPercentage = (num: number): string =>
            num % 1 > 0 ? num.toFixed(1) : Math.floor(num).toString();

          const timeDisplay = formatTimeValue(params.data.hours);
          const percentage = formatPercentage(params.data.percentage);
          return `${params.data.name}: ${timeDisplay} (${percentage}%)`;
        }
      },
      grid: {
        top: 0
      },
      series: [
        {
          type: 'pie',
          radius: ['40%', '70%'],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 10,
            borderColor: '#fff',
            borderWidth: 2
          },
          label: {
            show: false,
          },
          emphasis: {
            label: {
              show: true,
              fontSize: 16,
              fontWeight: 'bold'
            }
          },
          labelLine: {
            show: false
          },
          data: this.summaries.map(({ name, hours, color, percentage }) => ({
            value: hours,
            name,
            itemStyle: {
              color
            },
            hours,
            percentage
          }))
        }
      ]
    };
  }
}
